/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { useContext, useEffect, useState } from 'react';
import { Breadcrumbs, Breadcrumb, Intent, Tag } from '@blueprintjs/core';
import { filter, selectDilDefinitionByName, get } from 'srs-middleware';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';
import { WorkspaceContext } from '/views/workspace/workspaceContext';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import LoadingIcon from 'components/loadingIcon';

const { updateTransientFilter, updateResult } = WorkspaceActionCreator;

/**
 * Filters is a "result provider".  It is responsible for:
 * 1) monitoring for changes in the queryString
 * 2) keeping the results in order based on those changes
 * @returns {*}updateTransientFilter
 * @constructor
 */
export const Filters = () => {
	const {
		state: {
			explorer: { selectedDefinitionName },
			transient: { filters: transientFilters = [], queryStrings, rootResult },
		},
		dispatch,
	} = useContext(WorkspaceContext);

	const [results, setResults] = useState([]);
	const [transientResult, setTransientResult] = useState(null);

	const renderFilter = ({ key, value }) => {
		return (
			<Breadcrumb>
				<Tag intent={transientResult ? Intent.NONE : Intent.PRIMARY}>{`${key} (${value.count})`}</Tag>
			</Breadcrumb>
		);
	};

	// in here, we watch for changes to the query strings
	// then create a series of results, finally dispatching the changes
	const { data, loading } = useAsyncCallback(async () => {
		if (!rootResult) return;

		// two possible paths:
		// 1) transient filter exists, apply it and be done
		// 2) transient filter does not exist, process the query strings, apply it and be done
		let results = [{ key: 'all', value: rootResult }];
		let previous = rootResult;
		const entries = Object.entries(queryStrings);
		for (let i = 0; i < entries.length; i++) {
			const [key, filterString] = entries[i];
			if (filterString !== null) {
				const result = await filter(previous, selectDilDefinitionByName(selectedDefinitionName), filterString);
				results.push({ key: key, value: result });
				previous = result;
			}
		}

		return { results, previous, rootResult };
	}, [rootResult, selectedDefinitionName, queryStrings, dispatch]);

	useEffect(() => {
		const { results: rawResults, previous, rootResult: rawRootResult } = data ?? {};
		if (rawResults && rawRootResult) {
			dispatch(updateResult(previous, rawRootResult));
			setResults(rawResults);

			// clear the transient result, if any
			if (transientResult) {
				dispatch(updateTransientFilter(transientResult.key, null));
				setTransientResult(null);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, dispatch]);

	// here we currently assume that we get a "result" filter in, which just
	// replaces the currently active result with this new one
	// whenever anything else changes, this result is stale and should be removed
	// it can't be saved, as the method by which it was created (e.g. text clustering)
	// is not deterministically reproducible.
	const { data: transientData } = useAsyncCallback(async () => {
		const entries = Object.entries(transientFilters);
		let result = null;
		if (entries.length === 0) {
			setTransientResult(null);
			return;
		}
		const [key, value] = entries[0];
		result = await get(`/SRS/Results/${value[0].id}`);

		return { key: key, result: result };
	}, [transientFilters]);

	// only update the result when we have a filter, we assume that the stale result
	// will be replace by some other means.
	useEffect(() => {
		if (transientData) {
			dispatch(updateResult(transientData.result, rootResult));
			setTransientResult(transientData);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [transientData, dispatch]);

	// trigger the removal of the transient filter
	const onRemove = key => {
		dispatch(updateResult(results.length ? results[results.length - 1].value : rootResult, rootResult));
		dispatch(updateTransientFilter(key, null));
		setTransientResult(null);
	};

	return (
		<div css={filterStyle}>
			<Breadcrumbs items={results || []} breadcrumbRenderer={renderFilter} />
			<div
				css={css`
					padding-left: 50px;
				`}
			/>
			{transientResult && (
				<Breadcrumb>
					<Tag
						intent={Intent.PRIMARY}
						onRemove={() => {
							onRemove(transientResult.key);
						}}
					>
						{`${transientResult.key} (${transientResult.result.count})`}
					</Tag>
				</Breadcrumb>
			)}
			{loading && <LoadingIcon size={15} css={loadingStyles} />}
		</div>
	);
};

const filterStyle = css`
	flex: 1 1 auto;
	height: 100%;
	display: flex;
	flex-direction: row;
	justify-content: stretch;
	//padding: 2px;
`;

const loadingStyles = css`
	flex: 0 0;
	margin-left: 5px;
`;
